// ═══════════════════════════════════════════════════════════════════════════════
// Query Status Component
// Generic wrapper for React Query states (loading/error/success)
// ═══════════════════════════════════════════════════════════════════════════════

import { ReactNode } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SkeletonCard } from '@/components/ui/skeleton-card';
import { toSafeUiError } from '@/lib/user-safe-error';

interface QueryStatusProps<T> {
  query: UseQueryResult<T, Error>;
  loading?: ReactNode;
  error?: (err: Error, retry: () => void) => ReactNode;
  children: (data: T) => ReactNode;
}

export function QueryStatus<T>({
  query,
  loading,
  error,
  children,
}: QueryStatusProps<T>): JSX.Element | null {
  // Show loading state
  if (query.isLoading || query.isFetching) {
    return <>{loading || <SkeletonCard />}</>;
  }

  // Show error state
  if (query.isError) {
    const safeError = toSafeUiError(query.error);

    return (
      <>
        {error?.(query.error, query.refetch) || (
          <Card
            className="border-destructive/50 bg-destructive/5 p-6"
            role="alert"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle
                className="h-5 w-5 shrink-0 text-destructive"
                aria-hidden="true"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-destructive">
                  {safeError.title}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {safeError.description} {safeError.recovery}
                </p>
                {safeError.requestId && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Request ID: {safeError.requestId}
                  </p>
                )}
                {safeError.retryable && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 min-h-11"
                    onClick={() => query.refetch()}
                  >
                    <RefreshCw className="mr-2 h-3 w-3" aria-hidden="true" />
                    Retry
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}
      </>
    );
  }

  // Success state - show data or empty state
  if (!query.data) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-muted-foreground">No data available</p>
      </Card>
    );
  }

  return <>{children(query.data)}</>;
}
